/* eslint-disable react/forbid-prop-types */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  MdAirlineSeatReclineNormal,
  MdCancel,
  MdCheckCircle,
  MdEventRepeat,
  MdInfoOutline,
  MdKeyboardArrowDown,
  MdKeyboardArrowUp,
  MdLuggage,
  MdMonetizationOn,
  MdOutlineFreeCancellation,
  MdOutlineWorkOutline,
  MdRestaurant,
} from 'react-icons/md';
import AppButton from '../app-button/AppButton';
import AppRadioInput from '../app-radio-input/AppRadioInput';
import {
  formatCurrency,
  getNumSeats,
  isEmpty,
} from '../../helper-utils/appUtils';
import {
  FARE_FEATURE_STATUS,
  FARE_FEATURE_TYPES,
  getFareInclusionDetails,
  getFareInclusionList,
} from '../../helper-utils/fareInclusionUtils';
import './FareCard.scss';

const FEATURE_ICONS = {
  [FARE_FEATURE_TYPES.CANCELLATION]: MdOutlineFreeCancellation,
  [FARE_FEATURE_TYPES.RESCHEDULE]: MdEventRepeat,
  [FARE_FEATURE_TYPES.MEAL]: MdRestaurant,
  [FARE_FEATURE_TYPES.SEAT]: MdAirlineSeatReclineNormal,
};

const INCLUSION_ICONS = {
  [FARE_FEATURE_STATUS.FREE]: MdCheckCircle,
  [FARE_FEATURE_STATUS.CHARGEABLE]: MdMonetizationOn,
  [FARE_FEATURE_STATUS.NOT_ALLOWED]: MdCancel,
  [FARE_FEATURE_STATUS.INFO]: MdInfoOutline,
};

// Number of inclusions shown before "View more".
const VISIBLE_INCLUSIONS_COUNT = 2;

/** Flattens a seat value / per leg list / per trip list into one list. */
const toSeatList = (seats) =>
  Array.isArray(seats) ? seats.flatMap(toSeatList) : [seats];

const toSeatCount = (seat) => {
  const count = parseInt(seat, 10);
  return Number.isFinite(count) && count > 0 ? count : null;
};

/**
 * Least seat count across all the legs / trips of a fare - that is the
 * number of seats actually bookable. Groups without seat info are dropped.
 *
 * @param {Array<{ label?: string, seats: any }>} seatGroups
 * @returns {{ leastSeats: number|null,
 *             groups: Array<{ label: string, seats: number[] }> }}
 */
export const getSeatsLeftInfo = (seatGroups = []) => {
  const groups = seatGroups
    .map(({ label = '', seats }) => ({
      label,
      seats: toSeatList(seats).map(toSeatCount).filter(Boolean),
    }))
    .filter((group) => group.seats.length > 0);
  const allSeats = groups.flatMap((group) => group.seats);
  return {
    leastSeats: allSeats.length > 0 ? Math.min(...allSeats) : null,
    groups,
  };
};

/**
 * "Class" info rows. Multi trip fares (bookingClass as a list per trip)
 * get one row per trip so no trip is left out.
 *
 * @param {Array} bookingClass per leg list, or per trip list of leg lists
 * @param {string[]} [tripLabels] e.g. ['Onward', 'Return']; default "Trip N"
 */
export const getClassInfoRows = (bookingClass, tripLabels = []) => {
  if (!Array.isArray(bookingClass) || bookingClass.length === 0) return [];
  const isPerTrip = bookingClass.some(Array.isArray);
  if (!isPerTrip) {
    return [
      { label: 'Class', value: bookingClass.join(','), className: 'class' },
    ];
  }
  const trips = bookingClass.map((tripClass) =>
    Array.isArray(tripClass) ? tripClass.join(',') : tripClass,
  );
  if (trips.length === 1) {
    return [{ label: 'Class', value: trips[0], className: 'class' }];
  }
  return trips.map((value, index) => ({
    label: `${tripLabels[index] || `Trip ${index + 1}`} Class`,
    value,
    className: 'class',
  }));
};

const getRefundableInfo = (refundableinfo, isSeriesFare) => {
  if (isSeriesFare) {
    return {
      label: 'Non Refundable / Non Changeable',
      modifier: 'non-refundable',
    };
  }
  if (refundableinfo === 'Y') {
    return { label: 'Refundable', modifier: 'refundable' };
  }
  if (refundableinfo === 'PR') {
    return { label: 'Partially Refundable', modifier: 'partial-refundable' };
  }
  return { label: 'Non Refundable', modifier: 'non-refundable' };
};

// Highlights the "SOTO" tag inside fare types like "ECONOMY CLASSIC - SOTO"
const renderFareType = (fareType) =>
  String(fareType)
    .split(/(\bSOTO\b)/i)
    .map((part, index) =>
      index % 2 === 1 ? (
        // eslint-disable-next-line react/no-array-index-key
        <span key={index} className="fare-card__fare-tag">
          {part}
        </span>
      ) : (
        part
      ),
    );

function FeatureChip({ feature, tooltipId }) {
  const Icon = FEATURE_ICONS[feature.type];
  const hasDetails = feature.details?.length > 0;
  const chip = (
    <div
      className={`fare-card__feature fare-card__feature--${feature.status} ${
        hasDetails ? 'fare-card__feature--has-details' : ''
      }`}
    >
      {Icon && <Icon className="fare-card__feature-icon" />}
      <div className="fare-card__feature-text">
        <span className="fare-card__feature-label">{feature.label}</span>
        <span className="fare-card__feature-value">{feature.value}</span>
      </div>
      {/* Tells the user a hover shows the slab wise breakup */}
      {hasDetails && <MdInfoOutline className="fare-card__feature-info" />}
    </div>
  );

  if (!hasDetails) return chip;

  return (
    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip id={tooltipId} className="fare-card__tooltip">
          {feature.details.map((detail, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={index}>{detail}</div>
          ))}
        </Tooltip>
      }
    >
      {chip}
    </OverlayTrigger>
  );
}

FeatureChip.propTypes = {
  feature: PropTypes.shape({
    type: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    details: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  tooltipId: PropTypes.string.isRequired,
};

function SeatsLeft({ seatGroups, tooltipId }) {
  const { leastSeats, groups } = getSeatsLeftInfo(seatGroups);
  if (leastSeats === null) return null;

  return (
    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip id={tooltipId} className="fare-card__tooltip">
          {groups.map((group) => (
            <div key={group.label || 'seats'}>
              {group.label || 'Seats left'}: {group.seats.join(', ')}
            </div>
          ))}
        </Tooltip>
      }
    >
      <div className="fare-card__seats-left">
        {getNumSeats(leastSeats)} seats left
      </div>
    </OverlayTrigger>
  );
}

SeatsLeft.propTypes = {
  seatGroups: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string, seats: PropTypes.any }),
  ).isRequired,
  tooltipId: PropTypes.string.isRequired,
};
const expandedFareKeys = new Set();

function InclusionList({ inclusions, storageKey }) {
  const [isExpanded, setIsExpanded] = useState(() =>
    expandedFareKeys.has(storageKey),
  );
  const hiddenCount = inclusions.length - VISIBLE_INCLUSIONS_COUNT;
  const visibleInclusions = isExpanded
    ? inclusions
    : inclusions.slice(0, VISIBLE_INCLUSIONS_COUNT);
  const handleToggle = () => {
    if (isExpanded) expandedFareKeys.delete(storageKey);
    else expandedFareKeys.add(storageKey);
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="fare-card__section">
      <div className="fare-card__section-title">Inclusions</div>
      <ul className="fare-card__inclusions">
        {visibleInclusions.map((inclusion) => {
          const Icon = INCLUSION_ICONS[inclusion.status];
          return (
            <li
              key={inclusion.key}
              className={`fare-card__inclusion fare-card__inclusion--${inclusion.status}`}
            >
              <Icon className="fare-card__inclusion-icon" />
              <span>{inclusion.text}</span>
            </li>
          );
        })}
      </ul>
      {hiddenCount > 0 && (
        <button
          type="button"
          className="fare-card__view-more"
          onClick={handleToggle}
          aria-expanded={isExpanded}
        >
          {isExpanded ? 'View less' : `View more (${hiddenCount})`}
          {isExpanded ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}
        </button>
      )}
    </div>
  );
}

InclusionList.propTypes = {
  storageKey: PropTypes.string.isRequired,
  inclusions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
    }),
  ).isRequired,
};

/**
 * Shared fare card used by all flight search result flows
 * (one way, domestic round trip, international round trip, multi city).
 *
 * Inclusions (cancellation, reschedule, meal, seat) and baggage are derived
 * from the fare's `inclusions` / `penalties`. Baggage falls back to the
 * values passed by the flow; anything without information is not rendered.
 */
function FareCard({
  fare,
  className,
  watermark,
  isSelected,
  onSelect,
  topActions,
  priceClassName,
  showInvoiceFare,
  fareBreakup,
  isSeriesFare,
  checkinFallback,
  cabinFallback,
  legacyInclusion,
  infoRows,
  seatGroups,
  links,
  onBook,
  bookButtonClassName,
  footer,
}) {
  const { features, baggage } = useMemo(
    () =>
      getFareInclusionDetails(fare, {
        checkinFallback,
        cabinFallback,
        legacyInclusion,
      }),
    [fare, checkinFallback, cabinFallback, legacyInclusion],
  );
  const inclusionList = useMemo(() => getFareInclusionList(fare), [fare]);
  const hasSeatsLeft = getSeatsLeftInfo(seatGroups).leastSeats !== null;
  const refundable = getRefundableInfo(fare.refundableinfo, isSeriesFare);
  // returnFareType is only set for international round trip fares.
  // [onward] when same as return, [onward, return] when they differ.
  const fareTypes =
    !isEmpty(fare.returnFareType) &&
    !isEmpty(fare.faretype) &&
    fare.returnFareType.toLowerCase() !== fare.faretype.toLowerCase()
      ? [fare.faretype, fare.returnFareType]
      : [fare.faretype];
  const visibleInfoRows = infoRows.filter((row) => !isEmpty(row.value));
  const hasBaggage = !isEmpty(baggage.checkin) || !isEmpty(baggage.cabin);
  const showTopBar = Boolean(onSelect || topActions);
  const chipFeatures = features.filter(
    (feature) => feature.type !== FARE_FEATURE_TYPES.MEAL,
  );

  return (
    <div
      className={`fare-card fareCard fare-component-watermark ${
        isSelected ? 'fare-card--selected' : ''
      } ${fare.isBrandedFare ? 'branded-border' : ''} ${className}`}
      data-watermark={watermark}
    >
      {showTopBar && (
        <div className="fare-card__topbar watermark-actions">
          {onSelect ? (
            <AppRadioInput
              id={fare.nextraflightkey}
              name={fare.nextraflightkey}
              radioClassName="radioButton"
              checked={isSelected}
              onChange={() => onSelect(fare)}
              labelText={isSelected ? 'Selected' : 'Select'}
            />
          ) : (
            <span />
          )}
          {topActions && (
            <div className="fare-card__top-actions">{topActions}</div>
          )}
        </div>
      )}

      <div className="fare-card__header">
        <div className="fare-card__price-row">
          <div className="fare-card__price-block watermark-actions">
            <div className="d-flex align-items-center gap-2">
              <span className={`fare-card__price totalFare ${priceClassName}`}>
                {formatCurrency(fare.totalfq)}
              </span>
              {fareBreakup}
            </div>
            {showInvoiceFare && (
              <span className="fare-card__net-fare">
                {formatCurrency(fare.totalnet)}
              </span>
            )}
          </div>
          <span
            className={`fare-card__refundable fare-card__refundable--${refundable.modifier} reFundableInfo`}
          >
            {refundable.label}
          </span>
        </div>
        {!isEmpty(fare.faretype) && (
          <div className="fare-card__fare-type fareType">
            {fareTypes.map((fareType, index) => (
              <React.Fragment key={fareType}>
                {index > 0 && (
                  <span className="fare-card__fare-type-separator">/</span>
                )}
                {renderFareType(fareType)}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      {chipFeatures.length > 0 && (
        <div className="fare-card__section fare-card__features">
          {features
            .filter((feature) => feature.type !== FARE_FEATURE_TYPES.MEAL)
            .map((feature) => (
              <FeatureChip
                key={feature.type}
                feature={feature}
                tooltipId={`fare-feature-${feature.type}-${fare.nextraflightkey}`}
              />
            ))}
        </div>
      )}

      {inclusionList.length > 0 && (
        // storageKey keeps "View more" open across remounts - see expandedFareKeys
        <InclusionList
          inclusions={inclusionList}
          storageKey={fare.nextraflightkey}
        />
      )}

      {hasBaggage && (
        <div className="fare-card__section">
          <div className="fare-card__section-title">Baggage</div>
          <div className="fare-card__baggage">
            {!isEmpty(baggage.checkin) && (
              <div className="fare-card__baggage-item">
                <MdLuggage className="fare-card__baggage-icon" />
                <span className="fare-card__row-label">Check In</span>
                <span className="fare-card__row-value checkIn">
                  {baggage.checkin}
                </span>
              </div>
            )}
            {!isEmpty(baggage.cabin) && (
              <div className="fare-card__baggage-item">
                <MdOutlineWorkOutline className="fare-card__baggage-icon" />
                <span className="fare-card__row-label">Cabin</span>
                <span className="fare-card__row-value cabin">
                  {baggage.cabin}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {(visibleInfoRows.length > 0 || hasSeatsLeft) && (
        <div className="fare-card__section fare-card__info">
          {visibleInfoRows.map((row) => (
            <div className="fare-card__row" key={row.label}>
              <span className="fare-card__row-label">{row.label}</span>
              <span className={`fare-card__row-value ${row.className || ''}`}>
                {row.value}
              </span>
            </div>
          ))}
          {hasSeatsLeft && (
            <SeatsLeft
              seatGroups={seatGroups}
              tooltipId={`fare-seats-${fare.nextraflightkey}`}
            />
          )}
        </div>
      )}

      <div className="fare-card__footer watermark-actions">
        {links && <div className="fare-card__links">{links}</div>}
        <AppButton
          btnText="Book Now"
          variant="primary"
          className={`fare-card__book-btn bookNow ${bookButtonClassName}`}
          onClick={() => onBook(fare)}
        />
        {footer}
      </div>
    </div>
  );
}

FareCard.propTypes = {
  fare: PropTypes.object.isRequired,
  onBook: PropTypes.func.isRequired,
  className: PropTypes.string,
  watermark: PropTypes.string,
  isSelected: PropTypes.bool,
  onSelect: PropTypes.func,
  topActions: PropTypes.node,
  priceClassName: PropTypes.string,
  showInvoiceFare: PropTypes.bool,
  fareBreakup: PropTypes.node,
  isSeriesFare: PropTypes.bool,
  checkinFallback: PropTypes.string,
  cabinFallback: PropTypes.string,
  legacyInclusion: PropTypes.object,
  infoRows: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.node,
      className: PropTypes.string,
    }),
  ),
  /** Seats per leg / trip; the least count is shown. [{ label, seats }] */
  seatGroups: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string, seats: PropTypes.any }),
  ),
  links: PropTypes.node,
  bookButtonClassName: PropTypes.string,
  footer: PropTypes.node,
};

FareCard.defaultProps = {
  className: '',
  watermark: '',
  isSelected: false,
  onSelect: null,
  topActions: null,
  priceClassName: 'primary-color',
  showInvoiceFare: false,
  fareBreakup: null,
  isSeriesFare: false,
  checkinFallback: '',
  cabinFallback: '',
  legacyInclusion: null,
  infoRows: [],
  seatGroups: [],
  links: null,
  bookButtonClassName: '',
  footer: null,
};

export default FareCard;
